import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Filter, X, Upload, Plus, Save, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import MetricsCards from "@/components/MetricsCards";
import FiltersBar from "@/components/FiltersBar";
import ProductTable from "@/components/ProductTable";
import ProductFormModal from "@/components/ProductFormModal";
import ImportCSVModal from "@/components/ImportCSVModal";
import ExportReportButton from "@/components/ExportReportButton";
import ReportsExplorer from "@/components/ReportsExplorer";
import GoogleSheetsSync from "@/components/GoogleSheetsSync";
import { toast } from "sonner";

import { API_URL } from "@/lib/api";
const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ userRole = "admin", globalTC, onTCChange }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ familias: [], subfamilias: [], lotes: [], proveedores: [] });
  const [availableSubfamilias, setAvailableSubfamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showReportsExplorer, setShowReportsExplorer] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [tipoCambio, setTipoCambio] = useState(null);
  const [tcInput, setTcInput] = useState("");
  const [tcSaving, setTcSaving] = useState(false);
  const [tcLastUpdated, setTcLastUpdated] = useState("");

  const [filters, setFilters] = useState({
    familia: "", subfamilia: "", loteInicio: "", loteFinal: "",
    stockMin: "", stockMax: "", estado: "", proveedor: ""
  });

  useEffect(() => {
    fetchProducts();
    fetchStats();
    fetchFilterOptions();
    fetchTC();
  }, []);

  // Sync local TC state with global TC prop
  useEffect(() => {
    if (globalTC !== null && globalTC !== undefined) {
      setTipoCambio(globalTC);
      setTcInput(String(globalTC));
    }
  }, [globalTC]);

  const fetchTC = async () => {
    try {
      const res = await axios.get(`${API}/settings/tc`);
      const tc = res.data.tc;
      setTipoCambio(tc);
      setTcInput(String(tc));
      if (res.data.updated_at) setTcLastUpdated(res.data.updated_at);
    } catch (err) {
      console.error("Error fetching TC:", err);
    }
  };

  const handleSaveTC = async () => {
    const val = parseFloat(tcInput);
    if (!val || val <= 0) { toast.error("El tipo de cambio debe ser mayor a 0"); return; }
    setTcSaving(true);
    try {
      const res = await axios.put(`${API}/settings/tc`, { tc: val });
      setTipoCambio(res.data.tc);
      setTcInput(String(res.data.tc));
      toast.success(res.data.message);
      fetchProducts();
      fetchStats();
      onTCChange?.(); // Notify parent to refresh global TC
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar TC");
    } finally {
      setTcSaving(false);
    }
  };

  useEffect(() => {
    if (filters.familia) {
      fetchSubfamiliasByFamilia(filters.familia);
    } else {
      setAvailableSubfamilias(filterOptions.subfamilias);
    }
  }, [filters.familia, filterOptions.subfamilias]);

  useEffect(() => { applyFilters(); }, [products, searchTerm, filters]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/products/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/filters`);
      setFilterOptions(response.data);
      setAvailableSubfamilias(response.data.subfamilias);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchSubfamiliasByFamilia = async (familia) => {
    try {
      const response = await axios.get(`${API}/subfamilias/${encodeURIComponent(familia)}`);
      setAvailableSubfamilias(response.data.subfamilias);
    } catch (error) {
      setAvailableSubfamilias([]);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...products];
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => p.codigo.toLowerCase().includes(search) || p.descripcion.toLowerCase().includes(search));
    }
    if (filters.familia) filtered = filtered.filter((p) => p.familia === filters.familia);
    if (filters.subfamilia) filtered = filtered.filter((p) => p.subfamilia === filters.subfamilia);
    if (filters.loteInicio) { const min = parseInt(filters.loteInicio, 10); if (!isNaN(min)) filtered = filtered.filter((p) => parseInt(p.lote, 10) >= min); }
    if (filters.loteFinal) { const max = parseInt(filters.loteFinal, 10); if (!isNaN(max)) filtered = filtered.filter((p) => parseInt(p.lote, 10) <= max); }
    if (filters.stockMin !== "") filtered = filtered.filter((p) => p.stock >= parseFloat(filters.stockMin));
    if (filters.stockMax !== "") filtered = filtered.filter((p) => p.stock <= parseFloat(filters.stockMax));
    if (filters.proveedor) filtered = filtered.filter((p) => p.proveedor === filters.proveedor);
    if (filters.estado) {
      filtered = filtered.filter((p) => {
        const s = p.stock;
        if (filters.estado === "sin_stock") return s === 0;
        if (filters.estado === "stock_bajo") return s > 0 && s < 10;
        if (filters.estado === "baja_rotacion") return s >= 10 && s <= 50;
        if (filters.estado === "normal") return s > 50;
        return true;
      });
    }
    setFilteredProducts(filtered);
  }, [products, searchTerm, filters]);

  const handleClearFilters = () => {
    setFilters({ familia: "", subfamilia: "", loteInicio: "", loteFinal: "", stockMin: "", stockMax: "", estado: "", proveedor: "" });
    setSearchTerm("");
    toast.success("Filtros limpiados");
  };

  const handleImportSuccess = () => {
    fetchProducts(); fetchStats(); fetchFilterOptions();
    toast.success("Datos actualizados correctamente");
  };

  const hasActiveFilters = () => {
    return filters.familia || filters.subfamilia || filters.loteInicio || filters.loteFinal || filters.stockMin || filters.stockMax || filters.estado || filters.proveedor || searchTerm;
  };

  const canEdit = userRole === "admin" || userRole === "supervisor";
  const handleProductCrudSuccess = () => { fetchProducts(); fetchStats(); fetchFilterOptions(); };
  const handleEditProduct = (product) => { setEditingProduct(product); setShowProductForm(true); };
  const handleNewProduct = () => { setEditingProduct(null); setShowProductForm(true); };

  return (
    <div className="space-y-5">
      {/* KPI Cards - First */}
      {stats && <MetricsCards stats={stats} filteredProducts={filteredProducts} tipoCambio={tipoCambio} userRole={userRole} />}

      {/* Action Bar: TC Widget + Reports + Import/Sync */}
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {(userRole === "admin" || userRole === "supervisor") && (
            <GoogleSheetsSync onSyncComplete={() => { fetchProducts(); fetchStats(); fetchFilterOptions(); }} />
          )}
          {canEdit && (
            <Button data-testid="new-product-button" onClick={handleNewProduct} className="amazon-btn-cta h-9 body-font">
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo Producto
            </Button>
          )}
          {(userRole === "admin" || userRole === "supervisor") && (
            <Button data-testid="import-csv-button" variant="default" onClick={() => setShowImportModal(true)} className="h-9 body-font bg-[#232F3E] hover:bg-[#37475A] text-white">
              <Upload className="w-4 h-4 mr-1.5" /> Actualizar Datos
            </Button>
          )}
          {userRole !== "viewer" && (
            <ExportReportButton
              products={filteredProducts}
              filters={filters}
              searchTerm={searchTerm}
              onOpenExplorer={() => setShowReportsExplorer(true)}
            />
          )}
        </div>
        {userRole === "admin" && tipoCambio !== null && (
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-1.5">
            <label className="text-sm text-muted-foreground font-medium whitespace-nowrap body-font">TC (Bs/USD):</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={tcInput}
              onChange={e => setTcInput(e.target.value)}
              className="w-24 h-8 text-sm text-center border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] bg-background text-foreground mono-font"
              data-testid="tipo-cambio-input"
            />
            <Button
              size="sm"
              disabled={tcSaving || parseFloat(tcInput) === tipoCambio}
              onClick={handleSaveTC}
              className="h-8 px-3 amazon-btn-cta text-xs"
              data-testid="save-tc-button"
            >
              {tcSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            </Button>
          </div>
        )}
      </div>

      {/* Search + Filters - Below KPI */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-64 max-w-xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            data-testid="global-search-input"
            type="text"
            placeholder="Buscar por codigo o descripcion..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 search-input body-font bg-card"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="toggle-filters-button" variant="outline" onClick={() => setShowFilters(!showFilters)} className="h-9 body-font">
            <Filter className="w-4 h-4 mr-1.5" /> Filtros
          </Button>
          {hasActiveFilters() && (
            <Button data-testid="clear-filters-button" variant="ghost" onClick={handleClearFilters} className="text-destructive hover:text-destructive hover:bg-destructive/10 body-font h-9">
              <X className="w-4 h-4 mr-1" /> Limpiar
            </Button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <FiltersBar filters={filters} setFilters={setFilters} filterOptions={filterOptions} availableSubfamilias={availableSubfamilias} />
      )}

      {/* Product Table */}
      <ProductTable
        products={filteredProducts}
        loading={loading}
        totalCount={products.length}
        tipoCambio={tipoCambio}
        canEdit={canEdit}
        showHistory={userRole === "admin"}
        userRole={userRole}
        onEdit={handleEditProduct}
        onRefresh={handleProductCrudSuccess}
      />

      <ImportCSVModal open={showImportModal} onOpenChange={setShowImportModal} onImportSuccess={handleImportSuccess} />
      <ReportsExplorer open={showReportsExplorer} onOpenChange={setShowReportsExplorer} />
      <ProductFormModal
        open={showProductForm}
        onOpenChange={setShowProductForm}
        product={editingProduct}
        familias={filterOptions.familias}
        tipoCambio={tipoCambio || 1}
        onSuccess={handleProductCrudSuccess}
      />
    </div>
  );
};

export default Dashboard;
